import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { useMobileChat } from '../../context/MobileChatContext';
import { formatTime as formatTimeUtil, formatWeekdayShort, formatDateTime, formatTimeFromDate } from '../../utils/dateFormatting';
import { hasDepotTimes } from '../../lib/docketTimes';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  ChevronLeft, 
  Calendar,
  Loader2,
  MapPin,
  Truck,
  User,
  Phone,
  Mail,
  Building2,
  FileText,
  Clock,
  Wrench,
  Home,
  CheckCircle2,
  ClipboardList,
  Navigation,
  Map,
  MessageSquare,
  ChevronDown,
  Package,
  Flag,
  Mountain,
  Download,
  Image,
  File,
  AlertTriangle,
  Trash2,
  RotateCcw,
  PenLine,
  Layers,
  ChevronRight
} from 'lucide-react';
import { format } from 'date-fns';
import { MobileJobConfirmBanner } from '../../components/workerAck/MobileJobConfirmBanner';
import SendJobConfirmationModal from '../../components/SendJobConfirmationModal';
import { AddRouteStopDialog } from '../../components/mobile/AddRouteStopDialog';
import { EditRouteStopDialog } from '../../components/mobile/EditRouteStopDialog';
import { WheelTimePicker } from '../../components/mobile/WheelTimePicker';
import { ImageLightbox } from '../../components/ui/image-lightbox';
import { StopArrivalBadge } from '../../components/StopArrivalBadge';
import { CompleteStopDialog } from '../../components/mobile/CompleteStopDialog';
import { CompleteProjectTaskDialog } from '../../components/mobile/CompleteProjectTaskDialog';
import { DeleteStopDialog } from '../../components/mobile/DeleteStopDialog';
import { StartSignoffDialog } from '../../components/mobile/StartSignoffDialog';
import { EmailDocketModal } from '../../components/mobile/EmailDocketModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';

// Fix for default marker icon in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom yellow marker for job location
const jobMarkerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-gold.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const MobileJobDetail = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { api, tenantSettings, operator, isTimesheetOnly, isWorker, isFlagEnabled } = useMobileAuth();
  const dateFilter = searchParams.get('date') || 'today';
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [coordinates, setCoordinates] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [jobForms, setJobForms] = useState([]);
  const [jobFiles, setJobFiles] = useState([]);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [showForms, setShowForms] = useState(false);
  const [selectedViewingDate, setSelectedViewingDate] = useState(null);
  const [mandatoryStatus, setMandatoryStatus] = useState({ all_complete: true, forms: [] });
  const [expandedStopId, setExpandedStopId] = useState(null);
  const [completingStop, setCompletingStop] = useState(null);
  const [completingTask, setCompletingTask] = useState(null);
  const [deletingStop, setDeletingStop] = useState(null);
  const [startSignoffOpen, setStartSignoffOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [emailDocketOpen, setEmailDocketOpen] = useState(false);

  // Calculate the viewing date based on dateFilter or selected date
  const getViewingDate = () => {
    // If user has selected a specific date, use that
    if (selectedViewingDate) {
      return selectedViewingDate;
    }
    
    const today = new Date();
    const localFmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (dateFilter === 'yesterday') {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return localFmt(yesterday);
    } else if (dateFilter === 'tomorrow') {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return localFmt(tomorrow);
    } else if (dateFilter === 'today') {
      return localFmt(today);
    }
    // If dateFilter is an actual date string (YYYY-MM-DD), use it directly
    return dateFilter;
  };

  useEffect(() => {
    if (isTimesheetOnly()) { navigate('/mobile/dashboard', { replace: true }); return; }
    fetchJobDetail();
    fetchJobForms();
    fetchJobFiles();
  }, [jobId, dateFilter, selectedViewingDate]);

  // Geocode the address when job data is loaded
  useEffect(() => {
    if (job) {
      geocodeAddress();
    }
  }, [job]);

  const fetchJobDetail = async () => {
    setLoading(true);
    try {
      const viewingDate = getViewingDate();
      // Get user's LOCAL date (not UTC) for accurate timezone handling
      const now = new Date();
      const userToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const response = await api.get(`/api/mobile/jobs/${jobId}?viewing_date=${viewingDate}&user_today=${userToday}`);
      setJob(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'You are no longer allocated to this job');
        navigate('/mobile/jobs', { replace: true });
        return;
      }
      toast.error('Failed to fetch job details');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobForms = async () => {
    try {
      const [templatesRes, submissionsRes, mandatoryRes] = await Promise.all([
        api.get('/api/form-templates'),
        api.get(`/api/form-submissions?job_id=${jobId}`),
        api.get(`/api/jobs/${jobId}/mandatory-forms-status`).catch(() => ({ data: { all_complete: true, forms: [] } }))
      ]);
      setMandatoryStatus(mandatoryRes.data);
      // Filter for job-usable, active forms
      let jobLinkedForms = templatesRes.data.filter(
        t => t.is_active && t.form_type !== 'standalone'
      );
      // Filter by equipment type if applicable
      if (job?.equipment_type) {
        jobLinkedForms = jobLinkedForms.filter(t =>
          !t.equipment_types || t.equipment_types.length === 0 || t.equipment_types.includes(job.equipment_type)
        );
      }
      // Attach submission count and check if already submitted
      const subs = submissionsRes.data || [];
      jobLinkedForms = jobLinkedForms.map(t => ({
        ...t,
        submission_count: subs.filter(s => s.template_id === t.id).length,
        submissions: subs.filter(s => s.template_id === t.id)
      }));
      setJobForms(jobLinkedForms);
    } catch (error) {
      console.error('Failed to fetch job forms:', error);
    }
  };

  const fetchJobFiles = async () => {
    try {
      const response = await api.get(`/api/mobile/jobs/${jobId}/files`);
      setJobFiles(response.data);
    } catch (error) {
      // Silently fail — files are supplementary
    }
  };


  const handleStartJobForm = (template) => {
    // Check if multiple submissions are blocked and already submitted
    if (template.allow_multiple_submissions === false && template.submission_count > 0) {
      toast.error('This form has already been submitted for this job');
      return;
    }
    navigate(`/mobile/forms/submit?templateId=${template.id}&jobId=${jobId}`);
  };

  // Build full address string from job data
  const getFullAddress = () => {
    if (!job) return '';
    
    const parts = [];
    
    // Use site address if available, otherwise use one-off address
    const street = job.site?.street_address || job.one_off_street;
    const suburb = job.site?.suburb || job.one_off_suburb;
    const state = job.site?.state || job.one_off_state;
    const postcode = job.site?.postcode || job.one_off_postcode;
    
    if (street) parts.push(street);
    if (suburb) parts.push(suburb);
    if (state) parts.push(state);
    if (postcode) parts.push(postcode);
    
    // Add Australia for better geocoding results
    if (parts.length > 0) {
      parts.push('Australia');
    }
    
    return parts.join(', ');
  };

  // Geocode the address using OpenStreetMap Nominatim (free service)
  const geocodeAddress = async () => {
    const address = getFullAddress();
    if (!address || address === 'Australia') {
      return;
    }
    
    setGeocoding(true);
    try {
      const encodedAddress = encodeURIComponent(address);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1`,
        {
          headers: {
            'User-Agent': 'EQUIPR-Mobile-App'
          }
        }
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        setCoordinates({
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
          displayName: data[0].display_name
        });
      }
    } catch (error) {
      console.error('Geocoding failed:', error);
    } finally {
      setGeocoding(false);
    }
  };

  // Open native maps app for directions
  const openDirections = () => {
    const address = getFullAddress();
    if (!address) {
      toast.error('No address available for this job');
      return;
    }
    
    // Encode the address for URL
    const encodedAddress = encodeURIComponent(address);
    
    // Detect platform and open appropriate maps app
    // This will work on both iOS and Android
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS) {
      // Apple Maps
      window.open(`maps://maps.apple.com/?daddr=${encodedAddress}`, '_blank');
    } else {
      // Google Maps (works on Android and as fallback)
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
    }
  };

  // Haulage stop actions
  const handleStopArrive = async (stopId) => {
    try {
      const res = await api.post(`/api/jobs/${jobId}/stops/${stopId}/arrive`);
      setJob(prev => ({ ...prev, stops: res.data.stops }));
      toast.success('Marked as arrived');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark arrived');
    }
  };

  const handleStopComplete = (stopId, stopLabel, workDescription, arrivedAt) => {
    setCompletingStop({ id: stopId, label: stopLabel, work_description: workDescription, arrived_at: arrivedAt });
  };

  const handleStopCompleted = (stops) => {
    setJob(prev => ({ ...prev, stops }));
    setCompletingStop(null);
  };

  const handleCompleteProjectTask = async (taskData) => {
    if (!completingTask) return;
    try {
      const res = await api.post(`/api/mobile/jobs/${jobId}/project-tasks/${completingTask.id}/complete`, {
        job_date: job?.viewing_date || getViewingDate(),
        ...taskData,
      });
      setJob(prev => ({ ...prev, project_tasks: res.data.project_tasks }));
      setCompletingTask(null);
      toast.success('Task completed');
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error('Task was already completed by another worker');
        // Refetch job to get latest state
        try {
          const refreshed = await api.get(`/api/mobile/jobs/${jobId}?viewing_date=${job?.viewing_date || getViewingDate()}`);
          setJob(refreshed.data);
        } catch {}
      } else {
        toast.error(err.response?.data?.detail || 'Failed to complete task');
      }
      setCompletingTask(null);
    }
  };


  const handleStopDelete = (stopId, stopLabel) => {
    setDeletingStop({ id: stopId, label: stopLabel });
  };

  const handleStopDeleted = (stops, routeSummary) => {
    setJob(prev => ({ ...prev, stops, route_summary: routeSummary ?? prev.route_summary }));
    setDeletingStop(null);
  };

  const handleStartSignoffSaved = (startSignoff) => {
    setJob(prev => ({ ...prev, start_signoff: startSignoff }));
  };


  const handleStopRestore = async (stopId) => {
    try {
      const res = await api.post(`/api/jobs/${jobId}/stops/${stopId}/restore`);
      setJob(prev => ({ ...prev, stops: res.data.stops, route_summary: res.data.route_summary }));
      toast.success('Stop restored');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore stop');
    }
  };

  const handleStopsUpdated = (stops, routeSummary) => {
    setJob(prev => ({ ...prev, stops, route_summary: routeSummary }));
  };

  // ── Depart / Arrived Depot (driver-recorded, rounded to 5 min server-side) ──
  const [depotSaving, setDepotSaving] = useState(false);
  const [editDepot, setEditDepot] = useState(null); // 'depart' | 'arrive' | null
  const [depotTimeInput, setDepotTimeInput] = useState('');
  const [depotKmInput, setDepotKmInput] = useState('');
  const [showDocketPrompt, setShowDocketPrompt] = useState(false);
  const [oversizeEdit, setOversizeEdit] = useState(false);
  const [oversizeChecked, setOversizeChecked] = useState(false);
  const [oversizeMetersInput, setOversizeMetersInput] = useState('');
  const [oversizeSaving, setOversizeSaving] = useState(false);

  const saveOversize = async () => {
    setOversizeSaving(true);
    try {
      const res = await api.patch(`/api/jobs/${jobId}/oversize`, {
        oversize: oversizeChecked,
        oversize_meters: oversizeChecked ? oversizeMetersInput : '',
      });
      setJob(prev => ({
        ...prev,
        oversize: res.data.oversize ?? oversizeChecked,
        oversize_meters: res.data.oversize_meters ?? (oversizeChecked ? oversizeMetersInput : null),
      }));
      setOversizeEdit(false);
      toast.success('Oversize updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update oversize');
    } finally {
      setOversizeSaving(false);
    }
  };

  const localNowHHMM = () => formatTimeFromDate(new Date(), '24h');
  const depotHHMM = (iso) => (iso ? iso.split('T')[1]?.slice(0, 5) : null);

  const onRoadLabel = () => {
    if (!job?.depot_departed_at || !job?.depot_arrived_at) return null;
    const dep = new Date(job.depot_departed_at);
    const arr = new Date(job.depot_arrived_at);
    let mins = Math.round((arr - dep) / 60000);
    if (isNaN(mins) || mins < 0) return null;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const recordDepot = async (kind, time, km) => {
    setDepotSaving(true);
    try {
      const body = { time: time || localNowHHMM() };
      if (km !== undefined && km !== null && `${km}`.trim() !== '' && !isNaN(Number(km))) {
        body.odometer_km = Number(km);
      }
      const res = await api.post(`/api/jobs/${jobId}/depot/${kind}`, body);
      setJob(prev => ({ ...prev, ...res.data }));
      setEditDepot(null);
      setDepotKmInput('');
      toast.success(kind === 'depart' ? 'Depart Depot recorded' : 'Arrived Depot recorded');
      if (kind === 'arrive') {
        // End of day — offer to create today's docket
        setShowDocketPrompt(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record depot time');
    } finally {
      setDepotSaving(false);
    }
  };

  const goCreateDocket = () => {
    setShowDocketPrompt(false);
    const d = job?.viewing_date || (job?.depot_arrived_at || '').split('T')[0] || job?.end_date || job?.start_date;
    navigate(`/mobile/docket/create/${jobId}${d ? `?date=${d}` : ''}`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return formatWeekdayShort(dateStr, tenantSettings?.date_format);
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return formatTimeUtil(timeStr, tenantSettings.time_format);
  };

  // Use backend's can_create_docket instead of local calculation
  // This properly handles multi-day jobs and date-specific docket checking

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--m-bg)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-[var(--m-bg)] flex items-center justify-center">
        <p className="text-[var(--m-text-secondary)]">Job not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--m-bg)] pb-40">
      {/* Header */}
      <div className="sticky top-0 z-[1000] safe-top bg-[var(--m-bg-card)]">
        <div className="bg-[var(--m-bg-card)] px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/mobile/jobs')} className="text-[var(--m-text-secondary)] p-2">
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-[var(--m-text)] font-bold">Job Details</h1>
                <p className="text-[var(--m-text-secondary)] text-xs">{formatDate(job.start_date)}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/mobile/dashboard')}
              className="text-[var(--m-text-secondary)] p-2"
              data-testid="home-btn"
            >
              <Home className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Job Actions Button */}
        <button
          className="w-full bg-[#ffd108] text-black font-bold py-3 px-4 flex items-center justify-center gap-2 shadow-md active:brightness-90 transition-all"
          onClick={() => setActionsOpen(true)}
          data-testid="mobile-actions-fab"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/></svg>
          JOB ACTIONS
          <ChevronDown className="h-5 w-5 rotate-180" />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Worker job-confirmation banner (sent / re-ack / confirmed) */}
        <MobileJobConfirmBanner allocation={job.allocation} onChange={fetchJobDetail} />

        {/* Send job confirmation email (office roles only) */}
        {!isWorker() && (
          <Button
            onClick={() => setConfirmOpen(true)}
            className="w-full bg-[var(--m-bg-card)] border border-[#ffd108]/40 text-[#ffd108] hover:bg-[var(--m-bg-elevated)]"
            data-testid="mobile-send-confirmation-btn"
          >
            <Mail className="h-4 w-4 mr-2" /> Send Job Confirmation
          </Button>
        )}

        {/* Time & Status */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#ffd108]/20 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-[#ffd108]" />
                </div>
                <div>
                  {job.depot_time && job.depot_time !== job.start_time && (
                    <p className="text-[#c9a227] text-sm font-medium mb-0.5">
                      Depot {formatTime(job.depot_time)}
                    </p>
                  )}
                  <p className="text-[#ffd108] font-bold text-xl">
                    {job.depot_time && job.depot_time !== job.start_time ? 'Start ' : ''}{formatTime(job.start_time)}{job.end_time ? ` - ${formatTime(job.end_time)}` : ''}
                  </p>
                  {job.time_overridden && (
                    <p className="text-[#c9a227] text-xs mt-0.5" data-testid="mobile-day-time-override">
                      Custom time for this day
                    </p>
                  )}
                  {/* Show multi-day info if applicable */}
                  {job.is_multi_day ? (
                    <p className="text-[var(--m-text-secondary)] text-sm">
                      {formatDate(job.start_date)} - {formatDate(job.end_date)}
                    </p>
                  ) : (
                    <p className="text-[var(--m-text-secondary)] text-sm">{formatDate(job.start_date)}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge className={job.status === 'docketed' ? 'bg-green-900 text-green-300 hover:bg-green-800' : 'bg-blue-900 text-blue-300 hover:bg-blue-800'}>
                  {job.status}
                </Badge>
                {job.is_multi_day && (
                  <Badge className="bg-purple-900 text-purple-300 text-xs hover:bg-purple-800">
                    Multi-day
                  </Badge>
                )}
                {job.operator_role === 'additional_worker' && job.worker_type_name && (
                  <Badge className="bg-cyan-900 text-cyan-300 text-xs hover:bg-cyan-800">
                    {job.worker_type_name}
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Date Selector for Multi-day Jobs */}
        {job.is_multi_day && job.job_dates_info && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-[#ffd108]" />
                  <span className="text-[var(--m-text)] text-sm font-medium">Viewing Date</span>
                </div>
                <Select
                  value={selectedViewingDate || job.viewing_date}
                  onValueChange={(value) => setSelectedViewingDate(value)}
                >
                  <SelectTrigger className="w-[180px] bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border-light)]">
                    {job.job_dates_info.map((dateInfo) => (
                      <SelectItem 
                        key={dateInfo.date} 
                        value={dateInfo.date}
                        className="text-[var(--m-text)] hover:bg-[var(--m-bg-elevated)]"
                      >
                        <div className="flex items-center gap-2">
                          <span>{format(new Date(dateInfo.date + 'T00:00:00'), 'EEE, d MMM')}</span>
                          {dateInfo.has_docket && (
                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                          )}
                          {dateInfo.is_today && (
                            <Badge className="bg-blue-900 text-blue-300 text-[10px] px-1 hover:bg-blue-800">Today</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Show docket status summary */}
              <div className="mt-3 flex items-center gap-4 text-xs">
                <span className="text-[var(--m-text-secondary)]">
                  {job.job_dates_info.filter(d => d.has_docket).length} of {job.job_dates_info.length} days docketed
                </span>
                {job.has_docket_for_date && (
                  <Badge className="bg-green-900 text-green-300 text-[10px] hover:bg-green-800">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Docketed
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Required (mandatory) job-linked forms — surfaced up top so drivers
            can see exactly which forms must be completed before docketing. */}
        {mandatoryStatus.total > 0 && (
          mandatoryStatus.all_complete ? (
            <div
              className="flex items-center gap-2 rounded-lg border border-green-600/40 bg-green-900/20 px-3 py-2"
              data-testid="mobile-required-forms-complete"
            >
              <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
              <span className="text-sm text-green-300">All required forms complete</span>
            </div>
          ) : (
            <Card className="bg-amber-950/30 border-amber-600/50" data-testid="mobile-required-forms-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-amber-200 text-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  Required Forms
                  <Badge className="ml-auto bg-amber-500/20 text-amber-300 border border-amber-500/40 text-xs hover:bg-amber-500/30">
                    {mandatoryStatus.completed}/{mandatoryStatus.total} complete
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-amber-300/80 -mt-1">Complete these before you can create a docket for this job.</p>
                {(mandatoryStatus.forms || []).map((f) => (
                  <div
                    key={f.template_id}
                    className="flex items-center justify-between gap-2 rounded-lg bg-[var(--m-bg)] p-3"
                    data-testid={`mobile-required-form-${f.template_id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {f.is_complete
                        ? <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
                        : f.awaiting_approval
                          ? <span className="h-2 w-2 rounded-full bg-amber-500 flex-shrink-0" />
                          : <span className="h-2 w-2 rounded-full bg-red-500 flex-shrink-0" />}
                      <span className={`text-sm truncate ${f.is_complete ? 'text-green-300' : f.awaiting_approval ? 'text-amber-200' : 'text-[var(--m-text)]'}`}>{f.template_name}</span>
                    </div>
                    {f.is_complete ? (
                      <span className="text-[11px] text-green-400 flex-shrink-0">Completed</span>
                    ) : f.awaiting_approval ? (
                      <span className="text-[11px] text-amber-400 flex-shrink-0" data-testid={`mobile-required-form-awaiting-${f.template_id}`}>Awaiting approval</span>
                    ) : (
                      <Button
                        size="sm"
                        className="btn-volt h-7 px-3 text-xs flex-shrink-0"
                        onClick={() => navigate(`/mobile/forms/submit?templateId=${f.template_id}&jobId=${jobId}`)}
                        data-testid={`mobile-required-form-complete-btn-${f.template_id}`}
                      >
                        Complete now
                      </Button>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )
        )}


        {/* Customer Info */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
              <Building2 className="h-4 w-4 text-[#ffd108]" />
              Customer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-[var(--m-text)] font-medium text-lg">{job.customer?.company_name}</p>
            {!job.customer_contact && job.customer?.contact_name && (
              <p className="text-[var(--m-text-secondary)] text-sm flex items-center gap-2">
                <User className="h-4 w-4" />
                {job.customer.contact_name}
              </p>
            )}
            {!job.customer_contact && job.customer?.phone && (
              <a href={`tel:${job.customer.phone}`} className="text-[#ffd108] text-sm flex items-center gap-2">
                <Phone className="h-4 w-4" />
                {job.customer.phone}
              </a>
            )}
          </CardContent>
        </Card>

        {/* Oversize Load (haulage / multi-stop) — driver-editable */}
        {/* Customer Sign-On — authorisation (haulage, when timing includes start or anytime) */}
        {(job.job_type === 'haulage' || job.requires_multi_stops) && ['start', 'both', 'anytime'].includes(job.signature_config?.timing) && (() => {
          const so = job.start_signoff;
          const signed = so && !so.skipped && (so.customer_name || so.signature);
          const skipped = so && so.skipped;
          const isAnytime = job.signature_config?.timing === 'anytime';
          const cardLabel = isAnytime ? 'Customer Authorisation' : 'Customer Sign-On';
          const emptyHint = isAnytime ? 'Capture the customer\'s authorisation at any point during the job.' : 'Capture the customer\'s authorisation before starting the run.';
          return (
            <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]" data-testid="start-signoff-card">
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-[var(--m-text)] text-sm font-medium">
                    <PenLine className={`h-4 w-4 ${signed ? 'text-green-400' : skipped ? 'text-amber-400' : 'text-[var(--m-text-muted)]'}`} />
                    {cardLabel}
                  </div>
                  <Button size="sm" variant={signed || skipped ? 'ghost' : 'default'} className={signed || skipped ? 'h-7 px-2 text-[var(--m-text-body)]' : 'h-7 px-3 bg-[#ffd108] text-black'} onClick={() => setStartSignoffOpen(true)} data-testid="start-signoff-open-btn">
                    {signed || skipped ? 'Edit' : 'Sign On'}
                  </Button>
                </div>
                {signed ? (
                  <div className="mt-2 flex items-center gap-3" data-testid="start-signoff-summary">
                    {so.signature && <img src={so.signature} alt="Signature" className="h-10 bg-white rounded border border-[var(--m-border-light)]" />}
                    <div className="text-xs text-[var(--m-text-secondary)]">Signed by <span className="text-[var(--m-text)]">{so.customer_name}</span></div>
                  </div>
                ) : skipped ? (
                  <p className="mt-2 text-xs text-amber-400" data-testid="start-signoff-summary">Skipped — {so.skip_reason === 'no_one_on_site' ? 'No one on site' : so.skip_reason === 'customer_refused' ? 'Customer refused' : 'Other'}{so.skip_notes ? ` (${so.skip_notes})` : ''}</p>
                ) : (
                  <p className="mt-2 text-xs text-[var(--m-text-muted)]">{emptyHint}</p>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {(job.job_type === 'haulage' || job.requires_multi_stops) && (
          <Card className="bg-[var(--m-bg-card)] border-amber-700/40">
            <CardContent className="p-3" data-testid="mobile-oversize-section">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-[var(--m-text)] text-sm font-medium">
                  <AlertTriangle className={`h-4 w-4 ${job.oversize ? 'text-amber-400' : 'text-[var(--m-text-muted)]'}`} />
                  Oversize Load
                </div>
                {!oversizeEdit ? (
                  <div className="flex items-center gap-2">
                    {job.oversize ? (
                      <span className="text-amber-400 text-sm font-semibold" data-testid="oversize-value">{job.oversize_meters ? `${job.oversize_meters} m` : 'Flagged'}</span>
                    ) : <span className="text-[var(--m-text-muted)] text-xs" data-testid="oversize-value">Not flagged</span>}
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-[var(--m-text-body)]" onClick={() => { setOversizeChecked(!!job.oversize); setOversizeMetersInput(job.oversize_meters || ''); setOversizeEdit(true); }} data-testid="oversize-edit-btn">Edit</Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 flex-wrap justify-end">
                    <button type="button" onClick={() => setOversizeChecked(v => !v)} className={`h-7 px-2 rounded text-xs font-medium ${oversizeChecked ? 'bg-amber-500 text-black' : 'bg-[var(--m-bg)] text-[var(--m-text-secondary)] border border-[var(--m-border-light)]'}`} data-testid="oversize-toggle">{oversizeChecked ? 'Oversize ✓' : 'Not oversize'}</button>
                    {oversizeChecked && <input type="text" inputMode="decimal" value={oversizeMetersInput} onChange={(e) => setOversizeMetersInput(e.target.value)} placeholder="metres" className="w-20 bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded px-2 py-1 text-[var(--m-text)] text-sm" data-testid="oversize-meters-input" />}
                    <Button size="sm" className="h-7 px-2 bg-[#ffd108] text-black" disabled={oversizeSaving} onClick={saveOversize} data-testid="oversize-save-btn">{oversizeSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}</Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-[var(--m-text-secondary)]" onClick={() => setOversizeEdit(false)}>Cancel</Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contacts Section - Job Contact and Site Contact */}
        {(job.customer_contact || job.site_contacts?.length || job.site_contact) && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
                <User className="h-4 w-4 text-[#ffd108]" />
                Contacts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Job Contact (Customer Contact for this job) */}
              {job.customer_contact && (
                <div className="space-y-2">
                  <p className="text-xs text-[var(--m-text-muted)] uppercase tracking-wide">Job Contact</p>
                  <p className="text-[var(--m-text)] font-medium flex items-center gap-2">
                    {job.customer_contact.name}
                    {job.customer_contact.position && (
                      <span className="text-[var(--m-text-secondary)] text-sm">({job.customer_contact.position})</span>
                    )}
                  </p>
                  {job.customer_contact.phone && (
                    <a href={`tel:${job.customer_contact.phone}`} className="text-[#ffd108] text-sm flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {job.customer_contact.phone}
                    </a>
                  )}
                  {job.customer_contact.email && (
                    <a href={`mailto:${job.customer_contact.email}`} className="text-[var(--m-text-secondary)] text-sm flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {job.customer_contact.email}
                    </a>
                  )}
                </div>
              )}

              {/* Divider if both contacts exist */}
              {job.customer_contact && (job.site_contacts?.length || job.site_contact) && (
                <div className="border-t border-[var(--m-border)]" />
              )}

              {/* Site Contacts (supports multiple; falls back to legacy single) */}
              {(() => {
                const siteContactsList = job.site_contacts?.length ? job.site_contacts : (job.site_contact ? [job.site_contact] : []);
                if (!siteContactsList.length) return null;
                return (
                  <div className="space-y-3">
                    <p className="text-xs text-[var(--m-text-muted)] uppercase tracking-wide">Site Contact{siteContactsList.length > 1 ? 's' : ''}</p>
                    {siteContactsList.map((sc, idx) => (
                      <div key={sc.id || idx} className="space-y-2" data-testid={`mobile-site-contact-${sc.id || idx}`}>
                        <p className="text-[var(--m-text)] font-medium flex items-center gap-2">
                          {sc.name}
                          {sc.job_title && (
                            <span className="text-[var(--m-text-secondary)] text-sm">({sc.job_title})</span>
                          )}
                        </p>
                        {sc.phone && (
                          <a href={`tel:${sc.phone}`} className="text-[#ffd108] text-sm flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            {sc.phone}
                          </a>
                        )}
                        {sc.email && (
                          <a href={`mailto:${sc.email}`} className="text-[var(--m-text-secondary)] text-sm flex items-center gap-2">
                            <Mail className="h-4 w-4" />
                            {sc.email}
                          </a>
                        )}
                        {sc.notes && (
                          <p className="text-[var(--m-text-muted)] text-sm">{sc.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Location / Route Stops */}
        {(job.job_type === 'haulage' || job.requires_multi_stops) && job.stops && job.stops.length > 0 ? (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[#ffd108]" />
                Route Stops ({job.stops.filter(s => !s.deleted && s.status === 'completed').length}/{job.stops.filter(s => !s.deleted).length} completed)
              </CardTitle>
              {job.route_summary && <p className="text-[var(--m-text-secondary)] text-xs mt-1">{job.route_summary}</p>}
            </CardHeader>
            <CardContent className="space-y-2">
              {/* Depart Depot — start of route */}
              {(() => {
                const departed = depotHHMM(job.depot_departed_at);
                const activeStops = job.stops.filter(s => !s.deleted); const allCompleted = activeStops.length > 0 && activeStops.every(s => s.status === 'completed');
                return (
                  <div className="rounded-lg border border-[var(--m-border)] bg-[var(--m-bg-card)] p-3" data-testid="depot-depart-section">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-[var(--m-text)] text-sm font-medium">
                        <Truck className="h-4 w-4 text-[#ffd108]" />
                        Depart Depot
                      </div>
                      {departed && editDepot !== 'depart' ? (
                        <div className="flex items-center gap-2">
                          <span className="text-[#ffd108] text-sm font-semibold" data-testid="depot-departed-time">{formatTime(departed)}</span>
                          {job.depot_departed_km != null && <span className="text-[var(--m-text-secondary)] text-xs" data-testid="depot-departed-km">{job.depot_departed_km} km</span>}
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-[var(--m-text-body)]" onClick={() => { setDepotTimeInput(departed); setDepotKmInput(job.depot_departed_km != null ? String(job.depot_departed_km) : ''); setEditDepot('depart'); }} data-testid="depot-depart-edit-btn">Edit</Button>
                        </div>
                      ) : editDepot !== 'depart' ? (
                        <Button size="sm" className="h-8 bg-[#ffd108] text-black hover:bg-[#e6bd00]" disabled={depotSaving} onClick={() => { setDepotTimeInput(localNowHHMM()); setDepotKmInput(''); setEditDepot('depart'); }} data-testid="depot-depart-btn">
                          {depotSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Depart Now'}
                        </Button>
                      ) : null}
                    </div>
                    {editDepot === 'depart' && (
                      <div className="mt-2 space-y-2" data-testid="depot-depart-edit-row">
                        <div className="flex items-stretch gap-2">
                          <div className="flex-1 min-w-0 basis-0">
                            <WheelTimePicker value={depotTimeInput} onChange={setDepotTimeInput} minuteStep={5} className="w-full h-9 rounded-md bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] text-sm hover:bg-[var(--m-bg)] hover:text-[var(--m-text)]" testId="depot-depart-time-input" />
                          </div>
                          <div className="flex-1 min-w-0 basis-0">
                            <input type="number" inputMode="numeric" placeholder="KM" value={depotKmInput} onChange={(e) => setDepotKmInput(e.target.value)} className="w-full h-9 bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded-md px-3 text-[var(--m-text)] text-sm" data-testid="depot-depart-km-input" />
                          </div>
                        </div>
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="ghost" className="h-8 px-3 text-[var(--m-text-secondary)]" onClick={() => setEditDepot(null)}>Cancel</Button>
                          <Button size="sm" className="h-8 px-4 bg-[#ffd108] text-black" disabled={depotSaving} onClick={() => recordDepot('depart', depotTimeInput, depotKmInput)} data-testid="depot-depart-save-btn">Save</Button>
                        </div>
                      </div>
                    )}
                    {departed && <p className="text-[var(--m-text-muted)] text-xs mt-1">Route started — {job.depot_departed_by_name || 'Driver'}</p>}
                  </div>
                );
              })()}
              {[...job.stops].sort((a, b) => a.order - b.order).map((stop, idx) => {
                const status = stop.status || 'pending';
                const isPickup = stop.type === 'pickup';
                const isDropoff = stop.type === 'dropoff';
                const StopIcon = isPickup ? Package : isDropoff ? Flag : Navigation;
                const iconColor = status === 'completed' ? 'text-green-400' : status === 'arrived' ? 'text-blue-400' : 'text-[var(--m-text-muted)]';
                const isExpanded = expandedStopId === (stop.id || idx);
                const canAct = status !== 'completed';
                const isDeleted = !!stop.deleted;
                if (isDeleted) {
                  return (
                    <div
                      key={stop.id || idx}
                      className="rounded-lg border border-red-800/50 bg-red-950/20 p-3"
                      data-testid={`route-stop-${idx}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-red-900/40">
                          <StopIcon className="h-4 w-4 text-red-400/70" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] border-red-600 text-red-400 hover:bg-red-500/10">{stop.type}</Badge>
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-red-400" data-testid={`stop-${idx}-deleted-badge`}>Deleted</span>
                          </div>
                          <p className="text-[var(--m-text-secondary)] text-sm font-medium truncate mt-0.5 line-through">
                            {stop.label || stop.suburb || stop.address || `Stop ${idx + 1}`}
                          </p>
                          <p className="text-red-300/80 text-xs mt-1" data-testid={`stop-${idx}-deleted-reason`}>
                            Deleted by {stop.deleted_by_name || 'Driver'}{stop.deleted_reason ? ` — ${stop.deleted_reason}` : ''}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 px-3 border-[var(--m-border-light)] text-[var(--m-text-body)] hover:bg-[var(--m-bg-elevated)]/30 flex-shrink-0"
                          onClick={() => handleStopRestore(stop.id)}
                          data-testid={`stop-${idx}-restore-btn`}
                        >
                          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Undo
                        </Button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={stop.id || idx}
                    className={`rounded-lg border transition-all ${status === 'completed' ? 'border-green-700/50 bg-green-900/10' : status === 'arrived' ? 'border-blue-700/50 bg-blue-900/10' : 'border-[var(--m-border)] bg-[var(--m-bg)]'} cursor-pointer active:scale-[0.98]`}
                    onClick={() => setExpandedStopId(isExpanded ? null : (stop.id || idx))}
                    data-testid={`route-stop-${idx}`}
                  >
                    <div className="p-3">
                      <div className="flex items-center gap-3">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${status === 'completed' ? 'bg-green-900/50' : status === 'arrived' ? 'bg-blue-900/50' : 'bg-[var(--m-bg-card)]'}`}>
                          <StopIcon className={`h-4 w-4 ${iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={`text-[10px] ${status === 'completed' ? 'border-green-600 text-green-400 hover:bg-green-500/10' : status === 'arrived' ? 'border-blue-600 text-blue-400 hover:bg-blue-500/10' : 'border-[var(--m-border-light)] text-[var(--m-text-secondary)] hover:bg-[var(--m-bg-elevated)]'}`}>
                              {stop.type}
                            </Badge>
                            {stop.est_arrival && <span className="text-[var(--m-text-muted)] text-xs">ETA {stop.est_arrival}</span>}
                            <StopArrivalBadge stop={stop} dark />
                          </div>
                          <p className="text-[var(--m-text)] text-sm font-medium truncate mt-0.5">
                            {stop.label || stop.suburb || stop.address || `Stop ${idx + 1}`}
                          </p>
                          {stop.address && stop.suburb && <p className="text-[var(--m-text-secondary)] text-xs truncate">{stop.address}</p>}
                          {stop.notes && <p className="text-[var(--m-text-muted)] text-xs truncate">{stop.notes}</p>}
                          {stop.work_description && <p className="text-[var(--m-text-body)] text-xs mt-0.5" data-testid={`stop-${idx}-works`}><span className="text-[#ffd108]">Works:</span> {stop.work_description}</p>}
                          {(stop.site_contact?.name || stop.site_contact_name) && (
                            <div
                              className="flex items-center gap-1.5 mt-1 text-xs"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`stop-${idx}-site-contact`}
                            >
                              <User className="h-3 w-3 text-[#ffd108] flex-shrink-0" />
                              {stop.site_contact?.phone ? (
                                <a
                                  href={`tel:${stop.site_contact.phone}`}
                                  className="text-[#ffd108] truncate"
                                  data-testid={`stop-${idx}-site-contact-call`}
                                >
                                  {stop.site_contact.name}
                                  {stop.site_contact.job_title && <span className="text-[var(--m-text-muted)]"> ({stop.site_contact.job_title})</span>}
                                  <span className="text-[var(--m-text-secondary)]"> · {stop.site_contact.phone}</span>
                                </a>
                              ) : (
                                <span className="text-[var(--m-text-body)] truncate">
                                  {stop.site_contact?.name || stop.site_contact_name}
                                  {stop.site_contact?.job_title && <span className="text-[var(--m-text-muted)]"> ({stop.site_contact.job_title})</span>}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        {status === 'completed' ? (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <CheckCircle2 className="h-5 w-5 text-green-400" />
                            <ChevronDown className={`h-4 w-4 text-[var(--m-text-muted)] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>
                        ) : (
                          <ChevronDown className={`h-4 w-4 text-[var(--m-text-muted)] flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        )}
                      </div>
                    </div>
                    {/* Expanded action panel */}
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-1 border-t border-[var(--m-border)]/50 space-y-2" onClick={(e) => e.stopPropagation()}>
                        {status === 'pending' && (
                          <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700 text-[var(--m-text)]" onClick={() => handleStopArrive(stop.id)} data-testid={`stop-${idx}-arrive-btn`}>
                            <MapPin className="h-3.5 w-3.5 mr-1" /> Mark Arrived
                          </Button>
                        )}
                        {status === 'arrived' && (
                          <Button size="sm" className="w-full bg-green-600 hover:bg-green-700 text-[var(--m-text)]" onClick={() => handleStopComplete(stop.id, stop.suburb || stop.address || `Stop ${idx + 1}`, stop.work_description, stop.arrived_at)} data-testid={`stop-${idx}-complete-btn`}>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Complete Stop
                          </Button>
                        )}
                        <EditRouteStopDialog jobId={jobId} stop={stop} api={api} onUpdated={handleStopsUpdated} country={job.country || 'AU'} />
                        {/* Get Directions for this stop */}
                        {(stop.address || stop.suburb) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full border-[var(--m-border-light)] text-[var(--m-text-body)]"
                            onClick={() => {
                              const addr = [stop.address, stop.suburb, stop.state, stop.postcode].filter(Boolean).join(', ');
                              const encoded = encodeURIComponent(addr);
                              const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                              window.open(isIOS ? `maps://maps.apple.com/?daddr=${encoded}` : `https://www.google.com/maps/dir/?api=1&destination=${encoded}`, '_blank');
                            }}
                          >
                            <Navigation className="h-3.5 w-3.5 mr-1" /> Get Directions
                          </Button>
                        )}
                        {/* Delete any not-yet-completed stop (with reason + audit) */}
                        {status !== 'completed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full border-red-700 text-red-400 hover:bg-red-900/20"
                            onClick={() => handleStopDelete(stop.id, stop.label || stop.suburb || stop.address || `Stop ${idx + 1}`)}
                            data-testid={`stop-${idx}-delete-btn`}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete Stop
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <AddRouteStopDialog
                jobId={jobId}
                stops={job.stops}
                api={api}
                onUpdated={handleStopsUpdated}
                country={job.country || 'AU'}
              />
              {/* Arrived Depot — end of route (enabled once all stops completed) */}
              {(() => {
                const arrived = depotHHMM(job.depot_arrived_at);
                const departed = !!job.depot_departed_at;
                const activeStops = job.stops.filter(s => !s.deleted); const allCompleted = activeStops.length > 0 && activeStops.every(s => s.status === 'completed');
                const canArrive = departed && allCompleted;
                return (
                  <div className="rounded-lg border border-[var(--m-border)] bg-[var(--m-bg-card)] p-3" data-testid="depot-arrive-section">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-[var(--m-text)] text-sm font-medium">
                        <Home className="h-4 w-4 text-[#ffd108]" />
                        Arrived Depot
                      </div>
                      {arrived && editDepot !== 'arrive' ? (
                        <div className="flex items-center gap-2">
                          <span className="text-[#ffd108] text-sm font-semibold" data-testid="depot-arrived-time">{formatTime(arrived)}</span>
                          {job.depot_arrived_km != null && <span className="text-[var(--m-text-secondary)] text-xs" data-testid="depot-arrived-km">{job.depot_arrived_km} km</span>}
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-[var(--m-text-body)]" onClick={() => { setDepotTimeInput(arrived); setDepotKmInput(job.depot_arrived_km != null ? String(job.depot_arrived_km) : ''); setEditDepot('arrive'); }} data-testid="depot-arrive-edit-btn">Edit</Button>
                        </div>
                      ) : editDepot === 'arrive' ? (
                        <div className="flex items-center gap-1 flex-wrap justify-end">
                          <WheelTimePicker value={depotTimeInput} onChange={setDepotTimeInput} minuteStep={5} className="w-28 bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[var(--m-bg)] hover:text-[var(--m-text)]" testId="depot-arrive-time-input" />
                          <input type="number" inputMode="numeric" placeholder="KM" value={depotKmInput} onChange={(e) => setDepotKmInput(e.target.value)} className="w-28 bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded px-2 py-1 text-[var(--m-text)] text-sm" data-testid="depot-arrive-km-input" />
                          <Button size="sm" className="h-7 px-2 bg-[#ffd108] text-black" disabled={depotSaving} onClick={() => recordDepot('arrive', depotTimeInput, depotKmInput)} data-testid="depot-arrive-save-btn">Save</Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-[var(--m-text-secondary)]" onClick={() => setEditDepot(null)}>Cancel</Button>
                        </div>
                      ) : (
                        <Button size="sm" className="h-8 bg-[#ffd108] text-black hover:bg-[#e6bd00] disabled:opacity-40" disabled={depotSaving || !canArrive} onClick={() => { setDepotTimeInput(localNowHHMM()); setDepotKmInput(''); setEditDepot('arrive'); }} data-testid="depot-arrive-btn">
                          {depotSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Arrived Now'}
                        </Button>
                      )}
                    </div>
                    {!arrived && !canArrive && (
                      <p className="text-[var(--m-text-muted)] text-xs mt-1">
                        {!departed ? 'Record Depart Depot first' : 'Complete all stops to enable'}
                      </p>
                    )}
                    {arrived && <p className="text-[var(--m-text-muted)] text-xs mt-1">Returned — {job.depot_arrived_by_name || 'Driver'}</p>}
                    {onRoadLabel() && (
                      <div className="mt-2 pt-2 border-t border-[var(--m-border)] flex items-center justify-between" data-testid="depot-on-road-summary">
                        <span className="flex items-center gap-1.5 text-[var(--m-text-body)] text-xs"><Clock className="h-3.5 w-3.5 text-[#ffd108]" /> Total on-road</span>
                        <span className="text-[var(--m-text)] text-sm font-semibold" data-testid="depot-on-road-value">{onRoadLabel()}</span>
                      </div>
                    )}
                    {job.depot_departed_km != null && job.depot_arrived_km != null && job.depot_arrived_km >= job.depot_departed_km && (
                      <div className="mt-2 pt-2 border-t border-[var(--m-border)] flex items-center justify-between" data-testid="depot-distance-summary">
                        <span className="flex items-center gap-1.5 text-[var(--m-text-body)] text-xs"><Truck className="h-3.5 w-3.5 text-[#ffd108]" /> Distance</span>
                        <span className="text-[var(--m-text)] text-sm font-semibold" data-testid="depot-distance-value">{job.depot_arrived_km - job.depot_departed_km} km</span>
                      </div>
                    )}
                  </div>
                );
              })()}
              <CompleteStopDialog
                open={!!completingStop}
                onOpenChange={(v) => { if (!v) setCompletingStop(null); }}
                jobId={jobId}
                stopId={completingStop?.id}
                stopLabel={completingStop?.label}
                initialWorkDescription={completingStop?.work_description}
                initialArrivedAt={completingStop?.arrived_at}
                showSignature={job.signature_config?.waypoint_signature_enabled !== false}
                api={api}
                onCompleted={handleStopCompleted}
              />
              <DeleteStopDialog
                open={!!deletingStop}
                onOpenChange={(v) => { if (!v) setDeletingStop(null); }}
                jobId={jobId}
                stopId={deletingStop?.id}
                stopLabel={deletingStop?.label}
                api={api}
                onDeleted={handleStopDeleted}
              />
              <CompleteProjectTaskDialog
                open={!!completingTask}
                onOpenChange={(v) => { if (!v) setCompletingTask(null); }}
                task={completingTask}
                job={job}
                tenantSettings={tenantSettings}
                onComplete={handleCompleteProjectTask}
              />
              <StartSignoffDialog
                open={startSignoffOpen}
                onOpenChange={setStartSignoffOpen}
                jobId={jobId}
                api={api}
                onSaved={handleStartSignoffSaved}
                timing={job?.signature_config?.timing}
              />
              {/* End-of-day: prompt to create today's docket once back at the depot */}
              <Dialog open={showDocketPrompt} onOpenChange={setShowDocketPrompt}>
                <DialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] max-w-[340px] rounded-2xl">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-[var(--m-text)]">
                      <Home className="h-5 w-5 text-[#ffd108]" /> Back at the depot
                    </DialogTitle>
                    <DialogDescription className="text-[var(--m-text-secondary)]">
                      That's your route done. Create today's docket now? Your depot-to-depot hours are pre-filled — review and submit.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter className="flex-row gap-2 sm:justify-end">
                    <Button variant="ghost" className="flex-1 text-[var(--m-text-body)]" onClick={() => setShowDocketPrompt(false)} data-testid="depot-docket-prompt-later">Not now</Button>
                    <Button className="flex-1 bg-[#ffd108] text-black hover:bg-[#e6bd00]" onClick={goCreateDocket} data-testid="depot-docket-prompt-create">Create docket</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        ) : (
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[#ffd108]" />
              Location
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-[var(--m-text)] font-medium">{job.site?.name || 'One-off Location'}</p>
              {job.site?.address && (
                <p className="text-[var(--m-text-secondary)] text-sm mt-1">{job.site.address}</p>
              )}
              {(job.site?.street_address || job.one_off_street) && (
                <p className="text-[var(--m-text-secondary)] text-sm mt-1">
                  {job.site?.street_address || job.one_off_street}
                  {(job.site?.suburb || job.one_off_suburb) && `, ${job.site?.suburb || job.one_off_suburb}`}
                  {(job.site?.state || job.one_off_state) && ` ${job.site?.state || job.one_off_state}`}
                  {(job.site?.postcode || job.one_off_postcode) && ` ${job.site?.postcode || job.one_off_postcode}`}
                </p>
              )}
            </div>

            {job.starting_location && (
              <div className="pt-3 border-t border-[var(--m-border)]" data-testid="worker-starting-location">
                <p className="text-[var(--m-text-secondary)] text-xs uppercase mb-1 flex items-center gap-1"><Navigation className="h-3 w-3 text-[#ffd108]" />Starting Location</p>
                <p className="text-[var(--m-text)] text-sm">{job.starting_location}</p>
              </div>
            )}

            {/* Map Preview */}
            <div className="rounded-lg overflow-hidden border border-[var(--m-border-light)]">
              {geocoding ? (
                <div className="h-40 bg-[var(--m-bg)] flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-[#ffd108]" />
                  <span className="ml-2 text-[var(--m-text-secondary)] text-sm">Loading map...</span>
                </div>
              ) : coordinates ? (
                <MapContainer
                  center={[coordinates.lat, coordinates.lng]}
                  zoom={15}
                  style={{ height: '160px', width: '100%' }}
                  zoomControl={false}
                  attributionControl={false}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[coordinates.lat, coordinates.lng]} icon={jobMarkerIcon}>
                    <Popup>
                      <div className="text-sm">
                        <strong>{job.site?.name || 'Job Location'}</strong>
                        <br />
                        {job.customer?.company_name}
                      </div>
                    </Popup>
                  </Marker>
                </MapContainer>
              ) : (
                <div className="h-40 bg-[var(--m-bg)] flex items-center justify-center">
                  <div className="text-center">
                    <Map className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                    <p className="text-[var(--m-text-muted)] text-sm">Map unavailable</p>
                    <p className="text-gray-600 text-xs">No address coordinates found</p>
                  </div>
                </div>
              )}
            </div>

            {/* Get Directions Button */}
            <Button 
              onClick={openDirections}
              className="w-full bg-[#ffd108] hover:bg-[#e6bc00] text-[#1a1a1a] font-medium"
            >
              <Navigation className="h-4 w-4 mr-2" />
              Get Directions
            </Button>
          </CardContent>
        </Card>
        )}

        {/* Equipment */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
              <Truck className="h-4 w-4 text-[#ffd108]" />
              Equipment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-[var(--m-text)] font-medium">{job.equipment?.name}</p>
            <p className="text-[var(--m-text-secondary)] text-sm">Asset ID: {job.equipment?.asset_id}</p>
            <Badge variant="outline" className="mt-2 border-[var(--m-border-light)] text-[var(--m-text-body)]">
              {job.equipment_type}
            </Badge>

            {/* Full rig — additional trailers + prime mover (B-Double / Road Train) */}
            {(job.additional_trailers?.length > 0 || job.prime_mover) && (
              <div className="mt-3 pt-3 border-t border-[var(--m-border)] space-y-2" data-testid="mobile-job-rig">
                {(job.additional_trailers || []).map((tr, idx) => (
                  <div key={tr.id || idx} className="flex items-center justify-between" data-testid={`mobile-job-trailer-${idx + 2}`}>
                    <div>
                      <p className="text-[var(--m-text)] text-sm font-medium">{tr.name}</p>
                      <p className="text-[var(--m-text-muted)] text-xs">Trailer {idx + 2} · {tr.asset_id}</p>
                    </div>
                    <Badge variant="outline" className="border-[var(--m-border-light)] text-[var(--m-text-secondary)] text-[10px]">Trailer</Badge>
                  </div>
                ))}
                {job.prime_mover && (
                  <div className="flex items-center justify-between" data-testid="mobile-job-prime-mover">
                    <div>
                      <p className="text-[var(--m-text)] text-sm font-medium">{job.prime_mover.name}</p>
                      <p className="text-[var(--m-text-muted)] text-xs">Prime Mover · {job.prime_mover.asset_id}</p>
                    </div>
                    <Badge variant="outline" className="border-[#ffd108]/40 text-[#ffd108] text-[10px]">Prime Mover</Badge>
                  </div>
                )}
              </div>
            )}

            {/* Combo asset line-up — the member assets packaged into this combo */}
            {job.combo_members?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[var(--m-border)] space-y-2" data-testid="mobile-job-combo-members">
                <p className="text-[var(--m-text-secondary)] text-xs font-medium flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5 text-[#ffd108]" />
                  Combo Line-up
                </p>
                {job.combo_members.map((cm, idx) => (
                  <div key={cm.id || idx} className="flex items-center justify-between" data-testid={`mobile-job-combo-member-${idx}`}>
                    <div>
                      <p className="text-[var(--m-text)] text-sm font-medium">{cm.name}</p>
                      <p className="text-[var(--m-text-muted)] text-xs">{cm.component_type_name}{cm.asset_id ? ` · ${cm.asset_id}` : ''}</p>
                    </div>
                    <Badge variant="outline" className="border-purple-500/40 text-purple-300 text-[10px]">Member</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Attachments */}
        {job.attachment_types && job.attachment_types.length > 0 && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
                <Wrench className="h-4 w-4 text-[#ffd108]" />
                Attachments Required
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {job.attachment_types.map((att) => (
                  <Badge key={att.id} variant="secondary" className="bg-[var(--m-bg-elevated)] text-[var(--m-text-body)]">
                    {att.name}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Workers Assigned */}
        {job.worker_allocations && job.worker_allocations.length > 0 && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
                <User className="h-4 w-4 text-[#ffd108]" />
                Workers Assigned
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {job.worker_allocations.map((wa, idx) => {
                  const workerType = job.worker_types?.find(wt => wt.id === wa.worker_type_id);
                  return (
                    <div key={wa.id || idx} className="flex items-center justify-between bg-[var(--m-bg)] p-3 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#ffd108]/20 flex items-center justify-center">
                          <User className="h-5 w-5 text-[#ffd108]" />
                        </div>
                        <div>
                          <p className="text-[var(--m-text)] font-medium">{wa.worker_name || 'Not Assigned'}</p>
                          {wa.worker_phone ? (
                            <a href={`tel:${wa.worker_phone}`} className="text-[#ffd108] text-sm hover:underline">
                              {wa.worker_phone}
                            </a>
                          ) : (
                            <p className="text-[var(--m-text-muted)] text-sm">No phone</p>
                          )}
                        </div>
                      </div>
                      <Badge variant="outline" className="border-[var(--m-border-light)] text-[var(--m-text-body)]">
                        {workerType?.name || 'Worker'}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Description */}
        {job.description && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-[#ffd108]" />
                Description
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[var(--m-text-body)] text-sm whitespace-pre-wrap break-words">{job.description}</p>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        {job.notes && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-[var(--m-text)] text-sm">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[var(--m-text-secondary)] text-sm">{job.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Project Tasks — shown when flag is on and job has a project */}
        {isFlagEnabled('projects') && job.project && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]" data-testid="project-tasks-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
                <Layers className="h-4 w-4 text-[#ffd108]" />
                Project Tasks
              </CardTitle>
              <div className="flex flex-wrap gap-2 text-xs text-[var(--m-text-secondary)]">
                <span className="font-medium text-[#ffd108]">{job.project.name}</span>
                {job.project.start_date && job.project.end_date && (
                  <span>{job.project.start_date} — {job.project.end_date}</span>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {(!job.project_tasks || job.project_tasks.length === 0) ? (
                <p className="text-xs text-[var(--m-text-muted)]">No tasks for this project</p>
              ) : (
                job.project_tasks.map((task, idx) => {
                  const isCompleted = task.status === 'completed';
                  const c = task.completion;
                  return (
                    <div
                      key={task.id}
                      className={`p-3 rounded-lg border ${isCompleted ? 'border-green-500/30 bg-green-500/5' : 'border-[var(--m-border)] bg-[var(--m-bg)]'}`}
                      data-testid={`project-task-${task.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                          <div className={`mt-1 h-2.5 w-2.5 rounded-full shrink-0 ${isCompleted ? 'bg-green-500' : 'bg-zinc-500'}`} />
                          <div className="min-w-0">
                            <p className={`text-sm ${isCompleted ? 'text-[var(--m-text-secondary)] line-through' : 'text-[var(--m-text)]'}`}>
                              {task.description}
                            </p>
                            {isCompleted && c && (
                              <div className="mt-1 space-y-0.5">
                                {c.operator_name && <p className="text-[10px] text-[var(--m-text-muted)] flex items-center gap-1"><User className="h-3 w-3" />{c.operator_name}</p>}
                                {c.job_date && <p className="text-[10px] text-[var(--m-text-muted)] flex items-center gap-1"><Calendar className="h-3 w-3" />{c.job_date}</p>}
                                {(c.start_time || c.finish_time) && (
                                  <p className="text-[10px] text-[var(--m-text-muted)] flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {c.start_time ? format(new Date(c.start_time), 'HH:mm') : '?'} - {c.finish_time ? format(new Date(c.finish_time), 'HH:mm') : '?'}
                                  </p>
                                )}
                                {(c.start_smu != null || c.finish_smu != null) && (
                                  <p className="text-[10px] text-[var(--m-text-muted)] flex items-center gap-1">
                                    <Wrench className="h-3 w-3" />
                                    SMU: {c.start_smu ?? '-'} → {c.finish_smu ?? '-'} {c.smu_unit || ''}
                                  </p>
                                )}
                                {(c.start_smu_2 != null || c.finish_smu_2 != null) && (
                                  <p className="text-[10px] text-[var(--m-text-muted)] flex items-center gap-1">
                                    <Wrench className="h-3 w-3" />
                                    SMU2: {c.start_smu_2 ?? '-'} → {c.finish_smu_2 ?? '-'} {c.smu_unit_2 || ''}
                                  </p>
                                )}
                                {c.work_description && (
                                  <p className="text-[10px] text-[var(--m-text-muted)] flex items-center gap-1">
                                    <FileText className="h-3 w-3" />{c.work_description}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {!isCompleted && job.can_complete_project_tasks && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-[var(--m-text)] shrink-0"
                            onClick={() => setCompletingTask(task)}
                            data-testid={`complete-task-btn-${task.id}`}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        )}

        {/* Files */}
        {jobFiles.length > 0 && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]" data-testid="job-files-section">
            <CardHeader className="pb-2">
              <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
                <FileText className="h-4 w-4 text-[#ffd108]" />
                Files ({jobFiles.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {jobFiles.map(file => {
                const isImage = file.content_type?.startsWith('image/');
                const isPdf = file.content_type === 'application/pdf';
                const FileIcon = isImage ? Image : isPdf ? FileText : File;
                const sizeKb = file.size_bytes < 1024 * 1024
                  ? `${(file.size_bytes / 1024).toFixed(0)} KB`
                  : `${(file.size_bytes / (1024 * 1024)).toFixed(1)} MB`;
                
                return (
                  <button
                    key={file.id}
                    onClick={() => window.open(file.url, '_blank')}
                    className="flex items-center gap-3 w-full p-3 rounded-lg bg-[var(--m-bg)] hover:bg-[var(--m-bg-elevated)] transition-colors text-left"
                    data-testid={`mobile-job-file-${file.id}`}
                  >
                    <div className="h-10 w-10 rounded-lg bg-[var(--m-bg-elevated)] flex items-center justify-center shrink-0">
                      <FileIcon className="h-5 w-5 text-[var(--m-text-secondary)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[var(--m-text)] text-sm font-medium truncate">{file.filename}</p>
                      <p className="text-[var(--m-text-muted)] text-xs">{sizeKb}</p>
                    </div>
                    <Download className="h-4 w-4 text-[var(--m-text-muted)] shrink-0" />
                  </button>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Submitted Docket Data - Only show docket for current viewing date */}
        {job.viewing_date_docket && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)] border-l-4 border-l-green-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Submitted Docket - {formatDate(job.viewing_date)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {/* Docket Header */}
                <div className="flex items-center justify-between">
                  <Badge className="bg-green-900 text-green-300">
                    {job.viewing_date_docket.docket_number || 'Draft'}
                  </Badge>
                  <span className="text-xs text-[var(--m-text-secondary)]">
                    {job.viewing_date_docket.job_date}
                  </span>
                </div>

                {/* Docket Items - Filter out material items */}
                {job.viewing_date_docket.items && job.viewing_date_docket.items.filter(item => item.item_type !== 'material').length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-[var(--m-text-muted)] uppercase tracking-wide">Hours Recorded</p>
                    {job.viewing_date_docket.items.filter(item => item.item_type !== 'material').map((item, idx) => {
                      const isConcrete = item.item_type === 'concrete';
                      const isToll = item.item_type === 'toll';
                      const unitLabel = isConcrete ? (item.uom || 'm³') : 'h';
                      return (
                      <div key={item.id || idx} className="bg-[var(--m-bg)] p-2 rounded">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {item.item_type === 'equipment' ? (
                              <Truck className="h-3 w-3 text-[#ffd108]" />
                            ) : item.item_type === 'worker' ? (
                              <User className="h-3 w-3 text-blue-400" />
                            ) : isConcrete ? (
                              <Package className="h-3 w-3 text-orange-500" />
                            ) : (
                              <Wrench className="h-3 w-3 text-[var(--m-text-secondary)]" />
                            )}
                            <span className="text-[var(--m-text-body)] text-sm">{item.display_name || item.type_name}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-[#ffd108] font-medium text-sm">{isToll ? `× ${item.qty}` : `${item.qty}${isConcrete ? ` ${unitLabel}` : 'h'}`}</span>
                            {hasDepotTimes(item) ? (
                              <span className="text-[var(--m-text-muted)] text-xs ml-2">
                                Depot {formatTime(item.depart_depot_time)} → Site {formatTime(item.start_time)}–{formatTime(item.finish_time)} → Depot {formatTime(item.arrive_depot_time)}
                              </span>
                            ) : item.start_time && item.finish_time && (
                              <span className="text-[var(--m-text-muted)] text-xs ml-2">
                                ({formatTime(item.start_time)} - {formatTime(item.finish_time)})
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Lunch & Travel breakdown */}
                        {(item.lunch_minutes > 0 || item.travel_minutes > 0) && (
                          <div className="flex items-center gap-3 mt-1 ml-5 text-xs text-[var(--m-text-muted)]">
                            {item.lunch_minutes > 0 && (
                              <span>Lunch: -{item.lunch_minutes}min</span>
                            )}
                            {hasDepotTimes(item) && item.travel_out_minutes != null && item.travel_return_minutes != null
                              ? <span>Travel: {item.travel_out_minutes}+{item.travel_return_minutes}min</span>
                              : item.travel_minutes > 0 && <span>Travel: +{item.travel_minutes}min</span>
                            }
                          </div>
                        )}
                        {/* SMU (meter) readings — equipment items, when captured */}
                        {item.item_type === 'equipment' && (item.start_smu != null || item.finish_smu != null) && (
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 ml-5 text-xs text-[var(--m-text-secondary)]" data-testid={`mobile-docket-smu-${item.id || idx}`}>
                            <span>
                              <span className="text-[#ffd108]">Meter:</span>{' '}
                              <span className="font-mono text-[var(--m-text-body)]">{Number(item.start_smu ?? 0).toFixed(1)} → {Number(item.finish_smu ?? 0).toFixed(1)}</span>{' '}
                              <span className="text-[var(--m-text-muted)]">{item.smu_unit || 'Hours'}</span>
                            </span>
                            {item.start_smu != null && item.finish_smu != null && (
                              <span>
                                <span className="text-emerald-500">Δ</span>{' '}
                                <span className="font-mono text-[var(--m-text-body)]">{(Number(item.finish_smu) - Number(item.start_smu)).toFixed(1)}</span>
                              </span>
                            )}
                            {(item.start_smu_2 != null || item.finish_smu_2 != null) && (
                              <span>
                                <span className="text-blue-400">2nd:</span>{' '}
                                <span className="font-mono text-[var(--m-text-body)]">{Number(item.start_smu_2 ?? 0).toFixed(1)} → {Number(item.finish_smu_2 ?? 0).toFixed(1)}</span>{' '}
                                <span className="text-[var(--m-text-muted)]">{item.smu_unit_2 || 'KM'}</span>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}

                {/* Material Loads */}
                {job.viewing_date_docket.material_loads && job.viewing_date_docket.material_loads.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-[var(--m-text-muted)] uppercase tracking-wide flex items-center gap-1">
                      <Mountain className="h-3 w-3 text-orange-500" />
                      Material Loads
                    </p>
                    {job.viewing_date_docket.material_loads.map((load, idx) => (
                      <div key={load.id || idx} className="bg-[var(--m-bg)] p-2 rounded">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Package className="h-3 w-3 text-orange-500" />
                            <div>
                              <span className="text-[var(--m-text-body)] text-sm">{load.material_name}</span>
                              <p className="text-[var(--m-text-muted)] text-xs">{load.yard_name}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-orange-500 font-medium text-sm">{load.quantity} {load.unit}</span>
                            <p className={`text-xs ${
                              load.load_type === 'pickup' ? 'text-blue-400' :
                              load.load_type === 'disposal' ? 'text-amber-400' :
                              'text-green-400'
                            }`}>
                              {load.load_type === 'pickup' ? 'Pickup' : 
                               load.load_type === 'disposal' ? 'Disposal' : 'Buy Back'}
                            </p>
                          </div>
                        </div>
                        {load.tip_docket_number && (
                          <p className="text-[var(--m-text-muted)] text-xs mt-1 ml-5">Docket #: {load.tip_docket_number}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Work Completed */}
                {job.viewing_date_docket.work_completed && (
                  <div>
                    <p className="text-xs text-[var(--m-text-muted)] uppercase tracking-wide mb-1">Work Completed</p>
                    <p className="text-[var(--m-text-body)] text-sm bg-[var(--m-bg)] p-2 rounded">{job.viewing_date_docket.work_completed}</p>
                  </div>
                )}

                {/* Notes */}
                {job.viewing_date_docket.notes && (
                  <div>
                    <p className="text-xs text-[var(--m-text-muted)] uppercase tracking-wide mb-1">Docket Notes</p>
                    <p className="text-[var(--m-text-secondary)] text-sm bg-[var(--m-bg)] p-2 rounded">{job.viewing_date_docket.notes}</p>
                  </div>
                )}

                {/* Docket Photos */}
                {job.viewing_date_docket.photos && job.viewing_date_docket.photos.length > 0 && (
                  <div className="border-t border-[var(--m-border)] pt-3">
                    <p className="text-xs text-[var(--m-text-muted)] uppercase tracking-wide mb-2">Photos ({job.viewing_date_docket.photos.length})</p>
                    <div className="grid grid-cols-3 gap-2">
                      {job.viewing_date_docket.photos.map((photo, idx) => {
                        const url = typeof photo === 'string' ? photo : photo?.url;
                        return url ? (
                          <button key={idx} type="button" onClick={() => setLightboxUrl(url)} className="aspect-square rounded-lg overflow-hidden border border-[var(--m-border)] cursor-zoom-in">
                            <img src={url} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
                          </button>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

                {/* Customer Sign-off */}
                {job.viewing_date_docket.customer_signoff_confirmed && (
                  <div className="border-t border-[var(--m-border)] pt-3">
                    <p className="text-xs text-[var(--m-text-muted)] uppercase tracking-wide mb-2">Customer Sign-off</p>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <div>
                        <p className="text-[var(--m-text-body)] text-sm font-medium">{job.viewing_date_docket.customer_signoff_name || 'Customer'}</p>
                        <p className="text-[var(--m-text-muted)] text-xs">
                          {job.viewing_date_docket.customer_signoff_signature ? 'Confirmed & Signed' : 'Confirmed (signature not captured)'}
                        </p>
                      </div>
                    </div>
                    {job.viewing_date_docket.customer_signoff_signature && (
                      <button type="button" onClick={() => setLightboxUrl(job.viewing_date_docket.customer_signoff_signature)} className="mt-2 bg-white rounded p-2 inline-block cursor-zoom-in">
                        <img 
                          src={job.viewing_date_docket.customer_signoff_signature} 
                          alt="Customer Signature" 
                          className="h-12 max-w-[150px] object-contain"
                        />
                      </button>
                    )}
                  </div>
                )}
                {job.viewing_date_docket.signature_skipped && (
                  <div className="border-t border-[var(--m-border)] pt-3">
                    <p className="text-xs text-[var(--m-text-muted)] uppercase tracking-wide mb-2">Customer Sign-off</p>
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <div>
                        <p className="text-amber-400 text-sm font-medium">Signature Not Captured</p>
                        <p className="text-[var(--m-text-secondary)] text-xs">
                          {job.viewing_date_docket.signature_skip_reason === 'no_one_on_site' ? 'No one on site' : job.viewing_date_docket.signature_skip_reason === 'customer_refused' ? 'Customer refused' : 'Other'}
                        </p>
                      </div>
                    </div>
                    {job.viewing_date_docket.signature_skip_notes && (
                      <p className="text-[var(--m-text-muted)] text-xs mt-2">{job.viewing_date_docket.signature_skip_notes}</p>
                    )}
                  </div>
                )}

                {/* Project Task Completions — on submitted dockets */}
                {isFlagEnabled('projects') && job.viewing_date_docket.project_task_completions && job.viewing_date_docket.project_task_completions.length > 0 && (
                  <div className="border-t border-[var(--m-border)] pt-3" data-testid="submitted-docket-project-tasks">
                    <p className="text-xs text-[var(--m-text-muted)] uppercase tracking-wide mb-2 flex items-center gap-1">
                      <Layers className="h-3 w-3 text-[#ffd108]" /> Project Tasks Completed
                    </p>
                    <div className="space-y-2">
                      {job.viewing_date_docket.project_task_completions.map(task => (
                        <div key={task.id} className="flex items-start gap-2 p-2 rounded bg-green-500/5 border border-green-500/20" data-testid={`submitted-task-${task.id}`}>
                          <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                          <div className="text-xs">
                            <p className="text-[var(--m-text)] font-medium">{task.description}</p>
                            {task.completion && (
                              <div className="text-[var(--m-text-muted)] mt-0.5 space-y-0.5">
                                {task.completion.operator_name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{task.completion.operator_name}</span>}
                                {(task.completion.start_time || task.completion.finish_time) && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {task.completion.start_time ? new Date(task.completion.start_time).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '?'} - {task.completion.finish_time ? new Date(task.completion.finish_time).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '?'}
                                  </span>
                                )}
                                {task.completion.work_description && <span className="flex items-center gap-1"><FileText className="h-3 w-3" />{task.completion.work_description}</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Submitted Time */}
                <div className="text-xs text-[var(--m-text-muted)] text-right">
                  Submitted: {formatDateTime(job.viewing_date_docket.created_at, tenantSettings?.date_format, tenantSettings?.time_format)}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Slide-up Actions Menu */}
      {actionsOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-[999]" 
            onClick={() => setActionsOpen(false)}
          />
          <div className="fixed bottom-0 left-0 right-0 bg-[var(--m-bg-card)] border-t border-[var(--m-border)] rounded-t-2xl p-4 z-[1000] animate-in slide-in-from-bottom duration-200 space-y-2">
            {/* Close handle */}
            <button 
              className="w-full flex justify-center pb-2" 
              onClick={() => setActionsOpen(false)}
              data-testid="mobile-actions-close"
            >
              <div className="w-10 h-1 rounded-full bg-[var(--m-border-light)]" />
            </button>

            {/* Docket Action */}
            {job.is_multi_day && job.has_outstanding_dockets && !job.can_create_docket && job.has_docket_for_date ? (
              <div className="space-y-1">
                <Button className="w-full bg-green-900 text-green-300 cursor-default justify-start" disabled>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Docketed for {formatDate(job.viewing_date)}
                </Button>
                <p className="text-xs text-center text-[var(--m-text-secondary)]">
                  Select a different date above to create docket for another day
                </p>
              </div>
            ) : job.has_docket_for_date ? (
              <Button className="w-full bg-green-900 text-green-300 cursor-default justify-start" disabled>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Docket Submitted for {formatDate(job.viewing_date)}
              </Button>
            ) : job.can_create_docket ? (
              mandatoryStatus.all_complete ? (
              <Button 
                className="w-full btn-volt justify-start normal-case" 
                onClick={() => { setActionsOpen(false); navigate(`/mobile/docket/create/${job.id}?date=${job.viewing_date}${job.is_tower_crane ? '&tower_crane=true' : ''}`); }}
              >
                <FileText className="h-4 w-4 mr-2" />
                {job.is_tower_crane ? 'Create Daily Docket' : `Create Docket for ${formatDate(job.viewing_date)}`}
              </Button>
              ) : (
              <div className="space-y-1">
                <Button className="w-full justify-start" disabled variant="secondary">
                  <AlertTriangle className="h-4 w-4 mr-2 text-amber-400" />
                  Mandatory Forms Incomplete
                </Button>
                <p className="text-xs text-center text-amber-400">
                  {mandatoryStatus.completed}/{mandatoryStatus.total} required forms completed
                </p>
              </div>
              )
            ) : job.is_multi_day && job.has_outstanding_dockets ? (
              <div className="space-y-1">
                <Button className="w-full justify-start" disabled variant="secondary">
                  <Calendar className="h-4 w-4 mr-2" />
                  {job.docket_unavailable_reason || 'Cannot create docket for this date'}
                </Button>
                <p className="text-xs text-center text-[var(--m-text-secondary)]">
                  Select today or yesterday from the date picker above
                </p>
              </div>
            ) : (
              <Button className="w-full justify-start" disabled variant="secondary">
                <Calendar className="h-4 w-4 mr-2" />
                {job.docket_unavailable_reason || 'Docket not available'}
              </Button>
            )}

            {/* Email Docket */}
            {job.has_docket_for_date && job.docket_id_for_date && (
              <Button
                variant="outline"
                className="w-full bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] hover:bg-[#ffd108]/20 hover:border-[#ffd108]/60 justify-start"
                onClick={() => { setActionsOpen(false); setEmailDocketOpen(true); }}
                data-testid="mobile-email-docket-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                Email Docket
              </Button>
            )}

            {/* Job Forms */}
            {jobForms.length > 0 && (
              <Button
                variant="outline"
                className="w-full bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] hover:bg-purple-600 hover:border-purple-600 justify-start"
                onClick={() => { setActionsOpen(false); setShowForms(true); }}
              >
                <ClipboardList className="h-4 w-4 mr-2" />
                <span className="flex-1 text-left">Job Forms</span>
                <Badge variant="secondary" className="text-xs">{jobForms.length}</Badge>
              </Button>
            )}

            {/* Chat */}
            <Button 
              variant="outline"
              className="w-full bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] hover:bg-blue-600 hover:border-blue-600 justify-start"
              onClick={async () => { 
                setActionsOpen(false);
                try {
                  const response = await api.get(`/api/chat/job/${job.id}/room`);
                  if (response.data?.id) {
                    navigate(`/mobile/chat/${response.data.id}`);
                  } else {
                    navigate('/mobile/chat');
                  }
                } catch {
                  toast.error('Failed to open chat');
                }
              }}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Chat with Office
            </Button>
          </div>
        </>
      )}

      {/* Forms List Panel */}
      {showForms && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-[999]" 
            onClick={() => setShowForms(false)}
          />
          <div className="fixed bottom-0 left-0 right-0 bg-[var(--m-bg-card)] border-t border-[var(--m-border)] rounded-t-2xl p-4 z-[1000] animate-in slide-in-from-bottom duration-200 max-h-[70vh] flex flex-col">
            <button 
              className="w-full flex justify-center pb-2" 
              onClick={() => setShowForms(false)}
            >
              <div className="w-10 h-1 rounded-full bg-[var(--m-border-light)]" />
            </button>
            <h3 className="text-[var(--m-text)] font-bold text-sm mb-3 flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-purple-400" />
              Job Forms
            </h3>
            <div className="space-y-2 overflow-y-auto flex-1">
              {jobForms.map((form) => {
                const hasSubmission = form.submission_count > 0;
                const isBlocked = form.allow_multiple_submissions === false && hasSubmission;
                return (
                <div key={form.id} className="space-y-1">
                  <button
                    onClick={() => { if (!isBlocked) { setShowForms(false); handleStartJobForm(form); } }}
                    className={`w-full p-3 bg-[var(--m-bg)] rounded-lg text-left hover:bg-[var(--m-bg-elevated)] transition-colors flex items-center justify-between ${isBlocked ? 'opacity-70' : ''}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-[var(--m-text)] text-sm font-medium">{form.name}</p>
                        {form.is_mandatory && <span className="text-[10px] font-bold uppercase tracking-wide text-red-300 bg-red-500/20 border border-red-500/50 rounded px-1.5 py-0.5">Required</span>}
                      </div>
                      {form.description && (
                        <p className="text-xs text-[var(--m-text-secondary)] mt-0.5">{form.description}</p>
                      )}
                      {hasSubmission && !isBlocked && (
                        <p className="text-xs text-green-400 mt-1">
                          Completed ({form.submission_count}) — tap to submit again
                        </p>
                      )}
                      {isBlocked && (
                        <p className="text-xs text-green-400 mt-1">
                          Completed — single submission only
                        </p>
                      )}
                    </div>
                    {hasSubmission ? (
                      <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0 ml-2" />
                    ) : (
                      <FileText className="h-4 w-4 text-purple-400 flex-shrink-0 ml-2" />
                    )}
                  </button>
                  {/* List existing submissions as tappable links */}
                  {hasSubmission && (form.submissions || []).map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => { setShowForms(false); navigate(`/mobile/forms/submission/${sub.id}`); }}
                      className="w-full ml-4 mr-1 p-2 bg-[var(--m-bg-card)] rounded text-left hover:bg-[var(--m-bg-elevated)] transition-colors flex items-center justify-between"
                      data-testid={`job-submission-link-${sub.id}`}
                    >
                      <div className="flex items-center gap-2 text-xs text-[var(--m-text-body)] min-w-0">
                        <CheckCircle2 className="h-3 w-3 text-green-400 flex-shrink-0" />
                        <span className="truncate">
                          {sub.submitted_at ? format(new Date(sub.submitted_at), 'dd/MM/yyyy HH:mm') : 'Submitted'}
                        </span>
                        {sub.operator_name && <span className="text-[var(--m-text-muted)] truncate">— {sub.operator_name}</span>}
                      </div>
                      <ChevronRight className="h-3.5 w-3.5 text-[var(--m-text-muted)] flex-shrink-0" />
                    </button>
                  ))}
                </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {confirmOpen && (
        <SendJobConfirmationModal
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          jobId={job.id}
          api={api}
          onSent={() => { setConfirmOpen(false); fetchJobDetail(); }}
        />
      )}

      <EmailDocketModal
        open={emailDocketOpen}
        onClose={() => setEmailDocketOpen(false)}
        docketId={job.docket_id_for_date}
        api={api}
      />
      <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </div>
  );
};

export default MobileJobDetail;
